import { useEffect, useState } from "react";
import { CheckCircle2, ChevronDown, Loader2, MoreHorizontal, RotateCcw, Send, XCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { formatDateBR, formatDateTime, formatLocalDate } from "@/utils/dateUtils";
import { INVITE_STATUS_INFO, passosDaProposta, passosDoInvite } from "@/utils/proposta-ciclo";
import type {
  AcaoDoCiclo,
  CicloBody,
  InviteStatus,
  PropostaEvento,
  PropostaStatus,
} from "@/types/finance";

/** O que a equipe pediu para marcar: a proposta inteira ou o invite de um profissional. */
export interface PedidoDePasso {
  acao: AcaoDoCiclo;
  alvo: "proposta" | "invite";
  inviteId?: string;
  nome: string;
  sugestaoPara?: string;
}

const ROTULO_DO_PASSO: Record<"proposta" | "invite", Record<AcaoDoCiclo, string>> = {
  proposta: {
    enviado: "Marcar como enviada",
    aceito: "Marcar como aceita",
    recusado: "Marcar como recusada",
    reaberto: "Reabrir",
  },
  invite: {
    enviado: "Marcar como enviado",
    aceito: "Marcar como aceito",
    recusado: "Marcar como recusado",
    reaberto: "Reabrir",
  },
};

const ICONE_DO_PASSO: Record<AcaoDoCiclo, typeof Send> = {
  enviado: Send,
  aceito: CheckCircle2,
  recusado: XCircle,
  reaberto: RotateCcw,
};

interface AcoesDaPropostaProps {
  status: PropostaStatus;
  onPedir: (acao: AcaoDoCiclo) => void;
}

/** Próximo passo da proposta como botão principal e os outros num menu, na linha do título. */
export function AcoesDoCicloDaProposta({ status, onPedir }: AcoesDaPropostaProps) {
  const passos = passosDaProposta(status);
  if (passos.length === 0) return null;
  const [principal, ...outros] = passos;
  const IconePrincipal = ICONE_DO_PASSO[principal];

  return (
    <div className="flex">
      <Button
        className={outros.length > 0 ? "gap-1.5 rounded-r-none" : "gap-1.5"}
        variant={principal === "reaberto" ? "outline" : "default"}
        onClick={() => onPedir(principal)}
      >
        <IconePrincipal className="h-4 w-4" />
        {ROTULO_DO_PASSO.proposta[principal]}
      </Button>
      {outros.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button className="rounded-l-none border-l border-primary-foreground/20 px-2" aria-label="Outros passos">
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {outros.map((acao) => {
              const Icone = ICONE_DO_PASSO[acao];
              return (
                <DropdownMenuItem key={acao} onSelect={() => onPedir(acao)} className="gap-2">
                  <Icone className="h-4 w-4" />
                  {ROTULO_DO_PASSO.proposta[acao]}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}

interface AcoesDoInviteProps {
  status: InviteStatus;
  nome: string;
  onPedir: (acao: AcaoDoCiclo) => void;
}

export function AcoesDoCicloDoInvite({ status, nome, onPedir }: AcoesDoInviteProps) {
  const passos = passosDoInvite(status);
  if (passos.length === 0) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" size="icon" variant="ghost" className="h-7 w-7" aria-label={`Passos do invite de ${nome}`}>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {passos.map((acao) => {
          const Icone = ICONE_DO_PASSO[acao];
          return (
            <DropdownMenuItem key={acao} onSelect={() => onPedir(acao)} className="gap-2">
              <Icone className="h-4 w-4" />
              {ROTULO_DO_PASSO.invite[acao]}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function hoje(): string {
  return formatLocalDate(new Date()) ?? "";
}

function separarDestinatarios(texto: string): string[] {
  return texto
    .split(/[,;\n]/)
    .map((destinatario) => destinatario.trim())
    .filter(Boolean);
}

interface MarcarPassoDialogProps {
  pedido: PedidoDePasso | null;
  salvando: boolean;
  avisoInviteAntesDoAceite: boolean;
  onFechar: () => void;
  onConfirmar: (pedido: PedidoDePasso, body: CicloBody) => void;
}

export function MarcarPassoDialog({
  pedido,
  salvando,
  avisoInviteAntesDoAceite,
  onFechar,
  onConfirmar,
}: MarcarPassoDialogProps) {
  const [data, setData] = useState(hoje());
  const [para, setPara] = useState("");
  const [aceitoPor, setAceitoPor] = useState("");
  const [motivo, setMotivo] = useState("");
  const [observacao, setObservacao] = useState("");

  useEffect(() => {
    if (!pedido) return;
    setData(hoje());
    setPara(pedido.sugestaoPara ?? "");
    setAceitoPor("");
    setMotivo("");
    setObservacao("");
  }, [pedido]);

  if (!pedido) return null;

  const { acao, alvo } = pedido;
  const ehProposta = alvo === "proposta";
  const motivoObrigatorio = acao === "recusado";
  const podeConfirmar = !!data && (!motivoObrigatorio || !!motivo.trim()) && !salvando;
  const titulo = `${ROTULO_DO_PASSO[alvo][acao]}: ${pedido.nome}`;
  const descricao = {
    enviado: ehProposta
      ? "O envio é feito fora do sistema, com o PDF baixado na etapa Documento. Aqui fica o registro de quando e para quem."
      : "O envio é feito fora do sistema, com o PDF do invite. Aqui fica o registro de quando e para quem.",
    aceito: ehProposta
      ? "Registra o aceite da agência. As OS no Omie saem deste aceite quando o envio ao Omie for ligado."
      : "Registra que o profissional aceitou as condições do invite.",
    recusado: ehProposta
      ? "A proposta fica recusada e os invites que ainda não foram recusados são cancelados. Dá para reabrir depois."
      : "O invite fica recusado. Dá para reabrir depois.",
    reaberto: ehProposta
      ? "A proposta volta para gerada e pode ser editada e gerada de novo. Os invites cancelados pela recusa voltam ao que eram."
      : "O invite volta para gerado.",
  }[acao];

  const confirmar = () => {
    onConfirmar(pedido, {
      data,
      para: acao === "enviado" ? separarDestinatarios(para) : [],
      aceito_por: acao === "aceito" && ehProposta ? aceitoPor.trim() || null : null,
      motivo: motivo.trim() || null,
      observacao: observacao.trim() || null,
    });
  };

  return (
    <Dialog open onOpenChange={(aberto) => !aberto && onFechar()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{titulo}</DialogTitle>
          <DialogDescription>{descricao}</DialogDescription>
        </DialogHeader>
        {avisoInviteAntesDoAceite && !ehProposta && (acao === "enviado" || acao === "aceito") && (
          <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            A proposta ainda não foi aceita pela agência.
          </p>
        )}
        <div className="space-y-3">
          {acao !== "reaberto" && (
            <div className="space-y-1.5">
              <Label htmlFor="passo-data">Data</Label>
              <Input id="passo-data" type="date" max={hoje()} value={data} onChange={(evento) => setData(evento.target.value)} />
            </div>
          )}
          {acao === "enviado" && (
            <div className="space-y-1.5">
              <Label htmlFor="passo-para">Para quem</Label>
              <Input
                id="passo-para"
                value={para}
                onChange={(evento) => setPara(evento.target.value)}
                placeholder="Emails ou nomes, separados por vírgula"
              />
            </div>
          )}
          {acao === "aceito" && ehProposta && (
            <div className="space-y-1.5">
              <Label htmlFor="passo-aceito-por">Quem aceitou</Label>
              <Input
                id="passo-aceito-por"
                value={aceitoPor}
                onChange={(evento) => setAceitoPor(evento.target.value)}
                placeholder="Nome de quem aceitou na agência"
              />
            </div>
          )}
          {(acao === "recusado" || acao === "reaberto") && (
            <div className="space-y-1.5">
              <Label htmlFor="passo-motivo">{motivoObrigatorio ? "Motivo" : "Motivo (opcional)"}</Label>
              <Textarea id="passo-motivo" rows={3} value={motivo} onChange={(evento) => setMotivo(evento.target.value)} />
            </div>
          )}
          {acao !== "recusado" && acao !== "reaberto" && (
            <div className="space-y-1.5">
              <Label htmlFor="passo-observacao">Observação (opcional)</Label>
              <Textarea
                id="passo-observacao"
                rows={2}
                value={observacao}
                onChange={(evento) => setObservacao(evento.target.value)}
              />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onFechar}>
            Voltar
          </Button>
          <Button
            type="button"
            variant={acao === "recusado" ? "destructive" : "default"}
            disabled={!podeConfirmar}
            onClick={confirmar}
            className="gap-1.5"
          >
            {salvando && <Loader2 className="h-4 w-4 animate-spin" />}
            {ROTULO_DO_PASSO[alvo][acao]}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const VERBO_DO_EVENTO: Record<PropostaEvento["acao"], { proposta: string; invite: string }> = {
  enviado: { proposta: "Proposta enviada", invite: "Invite enviado" },
  aceito: { proposta: "Proposta aceita", invite: "Invite aceito" },
  recusado: { proposta: "Proposta recusada", invite: "Invite recusado" },
  reaberto: { proposta: "Proposta reaberta", invite: "Invite reaberto" },
  cancelado: { proposta: "Proposta cancelada", invite: "Invite cancelado" },
  enviado_omie: { proposta: "OS enviadas ao Omie", invite: "OS enviadas ao Omie" },
  cotacao_omie: { proposta: "Cotação das OS definida", invite: "Cotação das OS definida" },
};

export function HistoricoDoCiclo({ eventos }: { eventos: PropostaEvento[] }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Histórico de envio e aceite</CardTitle>
      </CardHeader>
      <CardContent>
        {eventos.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nada marcado ainda. Depois de gerar, marque aqui quando a proposta e cada invite forem enviados e aceitos.
          </p>
        ) : (
          <ol className="space-y-3">
            {eventos.map((evento) => {
              const verbo = VERBO_DO_EVENTO[evento.acao][evento.invite_id ? "invite" : "proposta"];
              const detalhes = [
                evento.para.length > 0 ? `para ${evento.para.join(", ")}` : null,
                evento.aceito_por ? `aceita por ${evento.aceito_por}` : null,
                evento.versao ? `versão ${evento.versao}` : null,
              ].filter(Boolean);
              return (
                <li key={evento.id} className="border-l-2 border-muted pl-3 text-sm">
                  <p>
                    <span className="font-medium">{verbo}</span>
                    {evento.freelancer_nome && <span className="text-muted-foreground"> · {evento.freelancer_nome}</span>}
                    <span className="text-muted-foreground"> em {formatDateBR(evento.ocorrido_em)}</span>
                  </p>
                  {detalhes.length > 0 && <p className="text-xs text-muted-foreground">{detalhes.join(" · ")}</p>}
                  {evento.motivo && <p className="text-xs text-muted-foreground">Motivo: {evento.motivo}</p>}
                  {evento.observacao && <p className="text-xs text-muted-foreground">{evento.observacao}</p>}
                  <p className="text-[11px] text-muted-foreground/80">
                    Registrado {formatDateTime(evento.registrado_em)}
                    {evento.registrado_por_nome ? ` por ${evento.registrado_por_nome}` : ""}
                  </p>
                </li>
              );
            })}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}

export function InviteStatusBadge({ status }: { status: InviteStatus }) {
  const info = INVITE_STATUS_INFO[status] ?? INVITE_STATUS_INFO.gerado;
  return (
    <Badge variant="outline" className={`text-[10px] ${info.badgeClass}`}>
      {info.label}
    </Badge>
  );
}
